<!--
  (c) Copyright 2024 Calabrio, Inc.
  All Rights Reserved. www.calabrio.com LICENSED MATERIALS
  Property of Calabrio, Inc., Minnesota, USA

  No part of this publication may be reproduced, stored or transmitted,
  in any form or by any means (electronic, mechanical, photocopying,
  recording or otherwise) without prior written permission from Calabrio, Inc.
-->

<template>
  <div class="wy-evaluation-new-question">
    <div v-if="showLockedModal&&isRoleAllow(4707)" class="locked-modal-wrapper">
      <div class="modal-body">
        <div>
          <h3>This question is currently being edited</h3>
          <div class="text email">
            The following user is already editing this question:
            {{ capitalizeFirstLetter(questionData.lockedBy) }}
          </div>
          <div class="text confirm">
            Are you sure you wish to proceed with your changes?
          </div>
        </div>
        <div class="btn-wrapper">
          <el-button :loading="isLockLoading" @click="handleLockQuestion">CONTINUE ANYWAYS</el-button>
          <el-button :disabled="isLockLoading" @click="handleBtnViewOnly">OPEN VIEW ONLY</el-button>
          <el-button :disabled="isLockLoading" class="cancel" @click="handleBtnClose">CANCEL</el-button>
        </div>
      </div>
    </div>

    <el-container :disabled="mode === 'view'" :class="{ 'read-only': mode === 'view' }">
      <div class="wy-evaluation-question-content" v-loading="loading">
        <div v-if="!loading" class="new-question-header">
          <el-input
            class="text-input question-name"
            placeholder="Question name"
            :maxlength="maxCharacterQuestionName"
            :disabled="loading || mode === 'view'"
            v-model="questionData.questionDisplayText"
            @focus="isQuestionNameEditing = true"
            @blur="isQuestionNameEditing = false"
          ></el-input>
          <div class="question-name-label" :class="{ disabled: loading || mode === 'view' }"> Question name <span style="color:red;">*</span></div>
          <div v-if="isQuestionNameEditing && questionData.questionDisplayText.length === maxCharacterQuestionName" class="warning-msg">
            0 characters remains
          </div>
          <div v-if="isQuestionNameEditing && questionData.questionDisplayText.length < maxCharacterQuestionName" class="reminding-msg">
            {{ maxCharacterQuestionName - questionData.questionDisplayText.length }} characters remains
          </div>
          <div v-if="mode === 'edit' || mode === 'new'" class="status-editable">
            Status: Editable
          </div>
          <div v-if="mode === 'view' && questionData.status === 'published'" class="status-published">
            Status: Published
          </div>
          <div v-if="mode === 'view' && questionData.status === 'archived'" class="status-published">
            Status: Archived
          </div>
          <div class="button-section">
            <button
              v-if="mode === 'edit'"
              data-testid="autoQM-customerQuestionEditor-deleteButton"
              class="btn-delete"
              @click="handleDeleteQuestion"
            >
              DELETE
            </button>
            <button
              v-if="mode === 'new' || mode === 'edit'"
              data-testid="autoQM-customerQuestionEditor-publishButton"
              class="btn-publish"
              :disabled="disablePublish"
              @click="handlePublishQuestion"
            >
              PUBLISH
            </button>
            <button
              v-if="mode === 'new' || mode === 'edit'"
              data-testid="autoQM-customerQuestionEditor-saveDraftButton"
              class="btn-save-draft"
              :disabled="disableSaveDraft"
              @click="handleSaveQuestion"
            >
              SAVE DRAFT
            </button>
            <button
              v-if="isRoleAllow(4707) && mode === 'view' && questionData.status === 'published' && inTask === 'No'"
              data-testid="autoQM-customerQuestionEditor-archiveButton"
              class="btn-save-draft"
              @click="handleArchiveQuestion"
            >
              ARCHIVE
            </button>
            <span
              class="btn-close el-icon-close"
              data-testid="autoQM-customerQuestionEditor-closeButton"
              @click="handleBtnClose"
            ></span>
          </div>
        </div>
        <div v-if="!loading" class="new-question-body" :class="{ 'read-only': mode === 'view' }">
          <div class="div-question-description-title">Question description <span style="color:red;">*</span>
            <el-tooltip
              popper-class="wy-evaluation-question-editor-question-description-popper"
              placement="bottom-start"
              :content="questionDescriptionTooltip"
            >
              <i class="question_info"></i>
            </el-tooltip>
          </div>
          <el-input
            v-if="!loading"
            class="text-input question-description"
            placeholder="Example: This question will evaluate if the agent followed the proper greeting protocol during the interaction."
            :maxlength="maxCharacterQuestionDes"
            :readonly="mode==='view'"
            type="textarea"
            resize="none"
            v-model="questionData.defaultDescription"
            @focus="isQuestionDesEditing = true"
            @blur="isQuestionDesEditing = false"
          ></el-input>
          <div class="question-description-label">Description</div>
          <div
            v-if="
              (mode==='new' || mode==='edit') && isQuestionDesEditing &&
                questionData.defaultDescription.length === maxCharacterQuestionDes
            "
            class="question-des-warning-msg"
          >
            0 characters remains
          </div>
          <div
            v-if="
              (mode==='new' || mode==='edit') && isQuestionDesEditing &&
                questionData.defaultDescription.length < maxCharacterQuestionDes
            "
            class="question-des-reminding-msg"
          >
            {{ maxCharacterQuestionDes - questionData.defaultDescription.length }} characters
            remains
          </div>
          <div
            v-if="mode === 'edit' || mode === 'view'"
            class="question-history-records"
          >
            <div class="author-info-title">Author information</div>
            <div class="author-info-des">
              This tells you who created and last updated the evaluation question.
            </div>
            <div class="createdBy-updatedBy">
              <div class="createdBy">
                <div class="createdBy-label">CREATED BY</div>
                <div class="createdBy-des">{{ questionData.creator }}</div>
              </div>
              <div class="updatedBy">
                <div class="updatedBy-label">LAST UPDATED BY</div>
                <div class="updatedBy-des">{{ questionData.updater }}</div>
              </div>
            </div>
          </div>
          <div class="options-section" v-if="questionData&&questionData.options&&questionData.options.length>0">
            <div class="div-option-title">Options</div>
            <div class="sub_description">For each Option added below, you can provide a description to further explain what the option is. This will be used by the AI to better identify the option when evaluating for this question.</div>
            <div class="option-title">
              <div class="option-body" :ref="'option' + optionIndex" v-for="(option, optionIndex) in questionData.options" :key="option.optionKey">
                <div v-if="option.optionDisplayText!=='N/A'" class="option-body-content">
                  <button
                    v-if="mode !== 'view'"
                    class="btn-move-question up"
                    :disabled="optionIndex === 0"
                    @click="moveOptionUp( optionIndex, questionData.options, $refs['option' + optionIndex])"
                  >
                    <i class="el-icon-arrow-up"></i>
                  </button>
                  <button
                    v-if="mode !== 'view'"
                    class="btn-move-question down"
                    :disabled="optionIndex === questionData.options.length - 1"
                    @click="moveOptionDown( optionIndex, questionData.options, $refs['option' + optionIndex])"
                  >
                    <i class="el-icon-arrow-down"></i>
                  </button>
                  <div class="option-label-content">
                    <el-input class="option-label" v-model="option.optionDisplayText" :maxlength="maxCharacterOptionName" show-word-limit :class="{'hasError': checkOptionTextDuplication(option.optionDisplayText)}"></el-input>
                    <span class="option-label-title">Option {{ optionIndex+1 }} <span style="color:red;">*</span></span>
                  </div>
                  <div class="div-phrase-content">
                    <div>
                      <el-input class="option-description" :maxlength="maxCharacterOptionDes" show-word-limit 
                        placeholder="Please describe this option and provide examples if necessary."
                        :readonly="mode==='view'" type="textarea" resize="none" v-model="option.optionDescription">
                      </el-input>
                    </div>
                    <span class="phrase-match-title">Option description <span style="color:red;">*</span></span>
                  </div>
                  <el-dropdown
                    v-if="mode !== 'view'"
                    ref="optionPopupMenu"
                    trigger="click"
                    placement="bottom-start"
                  >
                    <span class="el-dropdown-link">
                      <i class="el-icon-three-dots"></i>
                    </span>
                    <el-dropdown-menu
                      slot="dropdown"
                      class="elv-question-question-menu"
                    >
                      <el-dropdown-item
                        @click.native="
                          removeOption(questionData.options, optionIndex)
                        "
                        :disabled="questionData.options.length<=2"
                        >DELETE OPTION</el-dropdown-item
                      >
                    </el-dropdown-menu>
                  </el-dropdown>
                </div>
              </div>
            </div>
            <div v-if="!loading" class="option-body-bottom">
              <div class="div-na-content">
                <div class="na-option-content">
                  <el-checkbox data-testid="autoQM-customerQuestionEditor-naOptionEnableCheckbox" v-model="questionData.includeNAOption" @change="handleNAOptionEnabledChange">Include N/A option for this question.</el-checkbox>
                </div>
                <div v-if="questionData.includeNAOption && naOption" class="na-option-description-content">
                    <el-input class="option-description" :maxlength="maxCharacterOptionDes" show-word-limit 
                        placeholder="Please describe this option and provide examples if necessary."
                        :readonly="mode==='view'" type="textarea" resize="none" v-model="naOption.optionDescription">
                    </el-input>
                    <span class="phrase-match-title">Option description <span style="color:red;">*</span></span>
                </div>
              </div>
              <button v-if="questionData.options.length<optionsLimit" data-testid="autoQM-customerQuestionEditor-addOptionButton" class="btn-add-option" @click="addOption">ADD OPTION</button>
            </div>
          </div>
        </div>
      </div>
    </el-container>
    <div v-if="isPopupVisible" class="popup-wrapper">
      <div class="popup-body">
        <div class="title">Confirm</div>
        <div class="desc">{{ popupBodyText }}</div>
        <div class="footer">
          <el-button
            type="primary"
            size="mini"
            class="no-action"
            @click="isPopupVisible = false"
            >NO</el-button
          >
          <el-button type="primary" size="mini" @click="handlePopupConfirm"
            >YES</el-button
          >
        </div>
      </div>
    </div>
  </div>
</template>

<script>

import { mapGetters, mapActions } from "vuex";

export default {
  name: "wy-evaluation-new-question",
  modalTitle: null,
  modelContentStyle: "width: 100vw !important; max-width: unset !important; max-width: unset !important; height: 100vh; border-radius: 0px;",
  props: ["appVue", "questionManager"],
  data() {
    return {
      loading: true,
      questionId: null,
      input: "",
      mode: "",
      inTask: "No",
      // weight: false,
      questionList: [],
      categoryQuestions: [],
      questionListRef: null,
      popupBodyText: "",
      popupAction: "",
      isPopupVisible: false,
      isQuestionNameEditing: false,
      isQuestionDesEditing: false,
      isQuestionEditing: false,
      showLockedModal: false,
      isLockLoading: false,

      questionDescriptionTooltip: 'Please provide a Question Description to explain what this question is intended to evaluate. This will be used by the AI to better understand the question’s purpose and to provide a better evaluation',

      questionData: {},
      cloneQuestionData: {},

      naOption: {
        optionKey: 'na',
        optionDisplayText: 'N/A',
        optionDescription: '',
        defaultPoints: -1,
        defaultDisplayOrder: 1,
        autoFailLevel: null,
        isKpi: false,
        skipKpi: false
      },
      naOptionClone: {},
      preMsgObj: null,
      dialogVisible: false,
      maxCharacterQuestionName: 500,
      maxCharacterQuestionDes: 1000,
      maxCharacterOptionName: 30,
      maxCharacterOptionDes: 500,
      optionsLimit: 5,
    };
  },

  computed: {
    ...mapGetters(["authUser", "isRoleAllow"]),

    disableSaveDraft() {
      return (
        this.disableUpdate ||
        this.noQuestionDataChanged ||
        !this.isValidatedSave ||
        this.loading
      );
    },

    disablePublish() {
      return (
        this.disableUpdate ||
        (this.questionId === 0 && this.noQuestionDataChanged) ||
        !this.isValidatedSave ||
        this.loading
      );
    },

    disableUpdate() {
      if (this.questionData) {
        return (
          this.questionData.questionDisplayText === "" ||
          this.questionData.defaultDescription === "" ||
          this.questionData.questionDisplayText.length > this.maxCharacterQuestionName ||
          this.questionData.defaultDescription.length > this.maxCharacterQuestionDes
        );
      } else {
        return true;
      }
    },

    noQuestionDataChanged() {
      return (
        JSON.stringify(this.cloneQuestionData) === JSON.stringify(this.questionData) && (!this.questionData.includeNAOption || JSON.stringify(this.naOptionClone) === JSON.stringify(this.naOption))
      );
    },

    isValidatedSave() {
      let isValid = true;
      if (this.questionData.options && this.questionData.options.length > 0) {
        const displayTextSet = new Set();
        this.questionData.options.forEach((option) => {
          if (option.optionDisplayText === "" || displayTextSet.has(option.optionDisplayText.toLowerCase()) || option.optionDescription === "" || (option.optionDescription == null && option.optionKey !== 'na')) {
            isValid = false;
          }
          displayTextSet.add(option.optionDisplayText.toLowerCase());
        });
      }
      if(this.questionData.includeNAOption && this.naOption.optionDescription === "") {
        isValid = false;
      }
      return isValid;
    },
  },

  methods: {
    ...mapActions([
      "fetchCompanyProfileSettings",
      "fetchAutoQAEvalFormDropdown",
      // "fetchAutoQAEvalQuestionList",
      "fetchAutoQAEvalQuestionById",
      // "fetchAutoEvalFormList",
      "updateAutoEvalQuestion",
      "lockAutoEvalQuestion",
      "unlockAutoEvalQuestion",
    ]),
    handleNAOptionEnabledChange(includeNAOption) {
      if(!includeNAOption) {
        this.naOption.optionDescription = "";
      }
      this.questionData.includeNAOption = includeNAOption;
      this.questionData = JSON.parse(JSON.stringify(this.questionData))
    },

    capitalizeFirstLetter(string) {
      if (!string) return "";
      return string.charAt(0).toUpperCase() + string.slice(1);
    },

    handleBtnClose() {
      if (this.appVue) {
        if (
          !this.noQuestionDataChanged &&
          this.mode !== "view" &&
          !this.showLockedModal
        ) {
          this.isPopupVisible = true;
          this.popupAction = "unSavedQuestion";
          this.popupBodyText =
            "This action will cause you to lose any unsaved changes. Do you want to continue?";
        } else {
          if (this.questionListRef) {
            this.questionListRef.selectedActionRow = null;
          }
          if(this.questionListRef.pageName && this.questionListRef.pageName === "formEditorPage") {
            this.questionData = this.getDefaultFormData();
            this.mode = "new";
          }
          this.appVue.handleAppModalClose();
        }
      }
    },

    handleBtnViewOnly() {
      this.mode = "view";
      this.showLockedModal = false;
    },

    async handleLockQuestion() {
      this.isLockLoading = true;
      await this.lockAutoEvalQuestion({ questionId: this.questionId });
      this.isLockLoading = false;
      this.showLockedModal = false;
    },

    async handleUnlockQuestion() {
      this.isLockLoading = true;
      await this.unlockAutoEvalQuestion({ questionId: this.questionId });
      this.isLockLoading = false;
    },

    removeOption(options, index) {
      options.splice(index, 1);
      this.questionData = JSON.parse(JSON.stringify(this.questionData));
    },

    moveOptionUp(index, list, ref) {
      if (index > 0) {
        let cloneRef = ref[0];
        this.moveItem(list, index, index - 1);
        if (document.querySelector(".wy-evaluation-question-content")) {
          cloneRef.style.backgroundColor = "#eff4ff";
          if (cloneRef.classList.contains("fade-out-border-color")) {
            cloneRef.classList.remove("fade-out-border-color");
          }
          setTimeout(() => {
            document.querySelector(".wy-evaluation-question-content").scrollTop -=
            cloneRef.offsetHeight + 20;
            cloneRef.classList.add("fade-out-border-color");
          }, 20);
        }
      }
    },

    moveOptionDown(index, list, ref) {
      if (index < list.length - 1) {
        let cloneRef = ref[0];
        this.moveItem(list, index, index + 1);
        if (document.querySelector(".wy-evaluation-question-content")) {
          cloneRef.style.backgroundColor = "#eff4ff";
          if (cloneRef.classList.contains("fade-out-border-color")) {
            cloneRef.classList.remove("fade-out-border-color");
          }
          setTimeout(() => {
            document.querySelector(".wy-evaluation-question-content").scrollTop +=
            cloneRef.offsetHeight + 20;
              cloneRef.classList.add("fade-out-border-color");
          }, 20);
        }
        // this.$refs[scrollRef].scrollTop += ref[0].offsetHeight;
      }
    },

    moveItem(array, fromIndex, toIndex) {
      if (
        fromIndex < 0 ||
        fromIndex >= array.length ||
        toIndex < 0 ||
        toIndex >= array.length
      ) {
        return array;
      }
      const element = array.splice(fromIndex, 1)[0];
      array.splice(toIndex, 0, element);

      return array;
    },

    addOption() {
      let option = {
        optionKey: null,
        libraryQuestionId: this.questionId,
        optionDisplayText: "",
        optionDescription: "",
        autoFailLevel: null,
        defaultDisplayOrder: 1,
        defaultPoints: 0,
        isKpi: false,
        skipKpi: false,
      }
      this.questionData.options.push(option);
      this.questionData = JSON.parse(JSON.stringify(this.questionData));
    },

    checkOptionTextDuplication(text) {
      let count = 0;
      this.questionData.options.forEach((option) => {
        if (text.toLowerCase() === option.optionDisplayText.toLowerCase()) {
          count ++;
        }
      });
      if(count > 1) {
        return true
      }
      return false;
    },
    
    async handleDeleteQuestion() {
      this.isPopupVisible = true;
      this.popupAction = "delete";
      this.popupBodyText =
        "Deleting a question is permanent. Deleted questions cannot be used for future evaluations. Are you sure you want to delete this question?";
    },

    async handleArchiveQuestion() {
      this.isPopupVisible = true;
      this.popupAction = "archive";
      this.popupBodyText =
        "Archiving a question is permanent. Archived questions cannot be used for future evaluations. Are you sure you want to archive this question?";
    },

    async handlePopupConfirm() {
      if (this.popupAction === "delete") {
        await this.questionListRef.handleDelete();
        this.isPopupVisible = false;
      }
      if (this.popupAction === "archive") {
        this.isPopupVisible = false;
        this.loading = true;
        await this.questionListRef.handleArchive();
        this.questionData.status = "archived";
        this.loading = false;
      }
      if (this.popupAction === "unSavedQuestion") {
        this.isPopupVisible = false;
      }
      if (this.questionListRef) {
        this.questionListRef.selectedActionRow = null;
      }
      if (this.popupAction !== "archive") {
        if(this.questionListRef.pageName && this.questionListRef.pageName === "formEditorPage") {
          this.questionData = this.getDefaultFormData();
          this.mode = "new";
        }
        this.appVue.handleAppModalClose();
      }
    },

    async handlePublishQuestion() {
      let questionDataBackup = JSON.parse(JSON.stringify(this.questionData));
      if (this.publishValidation()) {
        
        let questionData = this.prepareWeightFormat();
        questionData.status = "published";
        this.loading = true;
        let res = await this.updateAutoEvalQuestion(questionData);
        // let res = {}
        if(this.questionListRef.getAutoQAEvalQuestionList) {
          await this.questionListRef.getAutoQAEvalQuestionList();
        }
        if(this.questionListRef.fetchQuestionManagerList) {
          this.questionListRef.fetchQuestionManagerList();
        }
        if (res.data) {
          if (res.status === 200) {
            if (this.preMsgObj == null) {
              this.preMsgObj = this.$message({
                dangerouslyUseHTMLString: false,
                duration: 3000,
                showClose: true,
                customClass: "evaluation-question-save-success-msg",
                message: "Publish Successful",
                onClose: () => {
                  this.preMsgObj = null;
                },
              });
            }
            this.questionData = res.data;
            this.questionData.status = "published";
            this.prepareQuestionData();
            if (!this.questionListRef.selectedActionRow) {
              this.questionListRef.selectedActionRow = this.questionData;
            }
            this.questionListRef.selectedActionRow.status = "published";
            this.mode = "view";
            if(this.questionListRef.getAutoQAEvalQuestionList) {
              this.questionListRef.getAutoQAEvalQuestionList();
            }
            if(this.questionListRef.fetchQuestionManagerList) {
              this.questionListRef.fetchQuestionManagerList();
            }
          } else if (res.status === 409) {
            let msg = this.$t(`errorCode.code${res.data.errorCode}`, {'lockedBy': res.data.lockedBy});
            this.questionData = JSON.parse(JSON.stringify(questionDataBackup));
            this.handleAlertMsg(msg);
          }
        }
        this.loading = false;
      }
    },

    prepareWeightFormat() {
      let questionData = JSON.parse(JSON.stringify(this.questionData));
      questionData.options.forEach((option, index) => {
        if(option.optionKey !== 'na') {
          option.optionKey = null;
        }
        option.defaultDisplayOrder = index + 1;
      })
      if(questionData.includeNAOption) {
        this.naOption.libraryQuestionId = questionData.questionId
        questionData.options.push(this.naOption);
      }
      return questionData;
    },
    generateKey(str) {
      return str
      .replace(/\s(.)/g, function($1) { return $1.toUpperCase(); })
      .replace(/\s/g, '')
      .replace(/^(.)/, function($1) { return $1.toLowerCase(); });
    },

    async handleSaveQuestion() {
      
      let questionData = this.prepareWeightFormat();
      questionData.status = "editable";
      this.loading = true;
      let res = await this.updateAutoEvalQuestion(questionData);
      // console.log(questionData)
      // let res = {}
      if (res.data) {
        if (res.status === 200) {
          if (this.preMsgObj == null) {
            this.preMsgObj = this.$message({
              dangerouslyUseHTMLString: false,
              duration: 3000,
              showClose: true,
              customClass: "evaluation-question-save-success-msg",
              message: "Save Successful",
              onClose: () => {
                this.preMsgObj = null;
              },
            });
          }
          this.mode = "edit";
          this.questionData = res.data;
          this.prepareQuestionData();
          if (!this.questionListRef.selectedActionRow) {
            this.questionListRef.selectedActionRow = this.questionData;
          }
          if(this.questionListRef.getAutoQAEvalQuestionList) {
            this.questionListRef.getAutoQAEvalQuestionList();
          }
          if(this.questionListRef.fetchQuestionManagerList) {
            this.questionListRef.fetchQuestionManagerList();
          }

          const isNewQuestion = this.questionId == null || this.questionId === 0;
          if (isNewQuestion) {
            this.questionId = res.data.questionId;
          }
        } else if (res.status === 409) {
          let msg = this.$t(`errorCode.code${res.data.errorCode}`, {'lockedBy': res.data.lockedBy});
          this.handleAlertMsg(msg);
        }
      } else {
        if (this.preMsgObj == null) {
          this.preMsgObj = this.$message({
            dangerouslyUseHTMLString: false,
            duration: 5000,
            showClose: true,
            customClass: "evaluation-question-internal-error-warning-msg",
            message: "Internal Error",
            onClose: () => {
              this.preMsgObj = null;
            },
          });
        }
      }
      this.loading = false;
    },

    prepareQuestionData() {
      if( this.questionData.options.length > 0) {
        this.naOption = (this.questionData.options.filter(option => option.optionKey === 'na').length > 0) 
                          ? this.questionData.options.filter(option => option.optionKey === 'na')[0]
                          : this.naOption;
        this.naOptionClone = JSON.parse(JSON.stringify(this.naOption));
        if(this.naOption && this.naOption.optionDescription == null) {
          this.naOption.optionDescription = "";
        }
        this.questionData.options = this.questionData.options.filter(option => option.optionKey !== 'na')
        this.questionData.options.forEach((option) => {
          if(option.optionDisplayText == null) {
            option.optionDisplayText = "";
          }
        });
        this.cloneQuestionData = JSON.parse(JSON.stringify(this.questionData));
      }
    },

    handleAlertMsg(msg) {
      if (this.preMsgObj == null) {
        this.preMsgObj = this.$message({
          dangerouslyUseHTMLString: false,
          duration: 5000,
          showClose: true,
          customClass: "evaluation-question-publish-warning-msg",
          message: msg,
          onClose: () => {
            this.preMsgObj = null;
          },
        });
      }
    },

    publishValidation() {
      let valid = true;
      if (this.questionData.options.length === 0) {
        let msg = "Unable to Question: There must be at least one option";
        this.handleAlertMsg(msg);
        valid = false;
      }
      
      return valid;
    },

    getDefaultFormData() {
      return  {
        "questionId": 0,
        "libraryCategoryId": 0,
        "questionDisplayText": "",
        "defaultDescription": '',
        "includeNAOption": false,
        "options": [
          {
            "optionId": 1,
            "optionKey": "yes",
            "optionDisplayText": "",
            "optionDescription": "",
            "optionExample": "",
            "defaultPoints": 0,
            "defaultDisplayOrder": 1,
            "autoFailLevel": null,
            "isKpi": false,
            "skipKpi": false
          },
          {
            "optionId": 2,
            "optionKey": "no",
            "optionDisplayText": "",
            "optionDescription": "",
            "defaultPoints": 0,
            "defaultDisplayOrder": 2,
            "autoFailLevel": null,
            "isKpi": false,
            "skipKpi": false
          }
        ]
      }
    },

    closeAllPopupMenus() {
      if (
        this.$refs.optionPopupMenu &&
        this.$refs.optionPopupMenu.length > 1
      ) {
        this.$refs.optionPopupMenu.forEach((item) => {
          item.hide();
        });
      }
      if (
        this.$refs.questionPopupMenu &&
        this.$refs.questionPopupMenu.length > 1
      ) {
        this.$refs.questionPopupMenu.forEach((item) => {
          item.hide();
        });
      }
    },
  },

  mounted() {
    window.addEventListener("resize", () => {
      this.closeAllPopupMenus();
    });
  },

  async created() {
    this.questionData = this.getDefaultFormData();
    this.questionListRef = this.appVue.appModalComponent.options;
    // if(this.questionListRef && this.questionListRef.pageName && this.questionListRef.pageName === "formEditorPage") {
    //   this.appVue.appModalComponent.modelContentStyle = "width: 80% !important; max-width: 1200px; max-width: unset !important; height: 60%; border-radius: 0px;";
    // }
    if (
      this.questionListRef &&
      this.questionListRef.selectedActionRow &&
      this.questionListRef.selectedActionRow.mode
    ) {
      this.mode = this.questionListRef.selectedActionRow.mode;
    } else {
      this.mode = "new";
    }
    if (
      this.questionListRef &&
      this.questionListRef.selectedActionRow &&
      this.questionListRef.selectedActionRow.inTask
    ) {
      this.inTask = this.questionListRef.selectedActionRow.inTask;
    }
    if (
      this.questionListRef &&
      this.questionListRef.selectedActionRow &&
      this.questionListRef.selectedActionRow.questionId
    ) {
      this.questionId = this.questionListRef.selectedActionRow.questionId;
      
      let res = await this.fetchAutoQAEvalQuestionById(this.questionId);
      if (res && res.data) {
        this.questionData = res.data;
        this.prepareQuestionData();
        this.mode = (this.questionData.status == "editable" && this.isRoleAllow(4707)) ? "edit" : "view";
      }
    } else {
      this.prepareQuestionData();
    }
    const isLocked = this.questionData.lockedBy != null;
    const isQuestionOwnerCurrentUser =
      this.questionData.lockedBy ==
      (this.authUser && this.authUser.user && this.authUser.user.email);

    this.loading = false;

    const isNewQuestion = this.questionId == null || this.questionId === 0;
    if (isNewQuestion) return;

    if (isLocked && !isQuestionOwnerCurrentUser) {
      this.showLockedModal = true;
    }
  },

  beforeDestroy() {
    const lockedModalIsVisible = this.showLockedModal;
    const isQuestionOwnerCurrentUser =
      this.questionData.lockedBy ==
      (this.authUser && this.authUser.user && this.authUser.user.email);

    const closeQuestionWhenQuestionAlreadyLockedBySomeoneElse =
      lockedModalIsVisible && !isQuestionOwnerCurrentUser;
    const closeQuestionInViewMode = this.mode == "view";

    if (closeQuestionWhenQuestionAlreadyLockedBySomeoneElse || closeQuestionInViewMode)
      return;

    if (this.mode == "edit") {
      this.handleUnlockQuestion();
    }
  },
};
</script>

<style lang="scss">
.wy-brand.wy-product-aq.wy-evaluationNewQuestion-page {
  overflow-x: auto;
  overflow-y: hidden;
}
.wy-evaluation-new-question {
  display: flex;
  // max-width: 1800px;
  min-width: 1200px;
  text-align: center;
  margin: auto;
  // height: 100%;
  height: calc(100% + 80px);
  .locked-modal-wrapper {
    position: fixed;
    height: 100%;
    width: 100%;
    left: 0;
    background-color: rgba(0, 0, 0, 0.2);
    z-index: 99999;

    display: flex;
    justify-content: center;
    align-items: center;

    .modal-body {
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      width: 600px;
      height: 200px;
      background-color: #fff;
      box-shadow: 0px 0px 10px 0px rgba(0, 0, 0, 0.2);
      padding: 20px 30px;
      text-align: left;
      color: #595959;
      h3 {
        color: black;
        font-weight: 500;
      }
      .text {
        font-size: 15px;
      }
      .confirm {
        margin-top: 20px;
      }
      .btn-wrapper {
        display: flex;
        justify-content: flex-end;
        .el-button {
          background-color: #fff;
          border-radius: 3px;
          border-color: #153a59;
          width: max-content;
          padding: 10px 14px;
          color: #153a59;
        }
        .cancel.el-button {
          background-color: #153a59;
          color: #fff;
        }
      }
    }
  }
  .wy-evaluation-question-content {
    min-width: 800px;
    overflow-y: auto;
    overflow-x: hidden;
    width: 100%;
    scroll-behavior: smooth;
    //// width: calc(100% - 400px);
  }
  .wy-evaluation-preview {
    // width: 400px;
    float: right;
  }
  .el-form {
    display: flex;
    max-width: 1800px;
    min-width: 1200px;
    text-align: center;
    // margin: auto;
  }
  .el-container {
    display: flex;
    // max-width: 1800px;
    min-width: 1200px;
    width: 100%;
    text-align: center;
    flex: unset;
  }
  .new-question-body.read-only {
    pointer-events: none;
    opacity: 0.7;
  }

  .new-question-header {
    // padding: 30px 40px;
    padding: 30px 0px 30px 40px;
    margin-right: 20px;
    border-bottom: 1px solid #000;
    text-align: left;
    position: sticky;
    top: 0;
    background-color: #fff;
    z-index: 999;
    .button-section {
      margin-left: auto;
      display: flex;
      float: right;
      position: relative;
      top: 5px;
      height: 30px;
      button {
        margin-right: 10px;
        border: 0px;
        box-shadow: none;
        border-radius: 2px;
        font-weight: 400;
        color: #000;
        background-color: #fff;
        border: 1px solid #d9d9d9;
        border-radius: 5px;
      }
      button:disabled {
        color: rgba(0, 0, 0, 0.26) !important;
        background-color: rgba(0, 0, 0, 0.12) !important;
        cursor: not-allowed;
      }
      .btn-delete {
        cursor: pointer;
        width: 92px;
      }
      .btn-publish {
        cursor: pointer;
        width: 92px;
      }
      .btn-save-draft {
        cursor: pointer;
        width: 112px;
        color: #fff;
        font-weight: 600;
        background-color: #003b5c;
      }
      .btn-save-draft:hover {
        background-color: #000;
      }
      .btn-close {
        cursor: pointer;
        font-size: 21px;
        font-weight: 700;
        float: right;
        margin-top: 5px;
      }
    }
    .text-input.question-name {
      max-width: 508px;
      font-size: 16px;
      width: calc(100% - 475px);
      input.el-input__inner {
        font-size: 16px;
        border-width: 1px !important;
        border-radius: 5px;
        padding-left: 10px;
        border-color: #d2d8dc;
      }
      .el-input.is-active .el-input__inner,
      .el-input__inner:focus {
        // border-color: #D2D8DC !important;
        border-color: #1a1c1e !important;
      }
    }
    .el-input.is-disabled .el-input__inner {
      background-color: #fff;
      color: #606266;
      opacity: 0.7;
      cursor: default;
    }
    .status-editable,
    .status-published {
      width: 90px;
      height: 20px;
      border-radius: 90px;
      padding: 5px 0px 0px 10px;
      margin-left: 15px;
      font-size: 11.2px;
      background-color: #efeef7;
      color: #3b3267;
      display: inline-flex;
      position: relative;
      top: -3px;
    }
    .status-published {
      width: 100px !important;
    }
    .question-name-label.disabled {
      opacity: 0.7;
    }
    .question-name-label {
      font-size: 11px;
      color: #444d54;
      position: absolute;
      top: 24px;
      left: 60px;
      background-color: #fff;
      padding: 0px 5px;
    }
    .warning-msg {
      font-size: 11px;
      color: #ebafba;
      position: absolute;
      top: 71px;
      left: 42px;
    }
    .reminding-msg {
      font-size: 11px;
      color: #444d54;
      position: absolute;
      top: 71px;
      left: 42px;
    }
  }
  .new-question-body.el-loading-parent--relative {
    height: calc(100% - 150px);
  }
  .new-question-body {
    padding: 20px 10px 30px 40px;
    // overflow-y: auto;
    // height: calc(100% - 115px);
    // scroll-behavior: smooth;
    // text-align: left;
    position: relative;
    scroll-behavior: smooth;
    text-align: left;

    .div-question-description-title {
      font-size: 20px;
      font-weight: 500;
      line-height: 28px;
      background: var(--question-editor-stars-icon-white);
      background-repeat: no-repeat;
      background-size: 25px;
      background-position: left;
      padding-left: 35px;
    }
    .question_info {
      background: var(--question-editor-question-mark-blue);
      background-repeat: no-repeat;
      background-size: 15px;
      width: 15px;
      height: 15px;
      background-position-y: 0px;
      margin-left: 5px;
      cursor: pointer;
      position: absolute;
      top: 28px;
      left: 283px;
      pointer-events: all !important;
    }

    .question-history-records {
      .author-info-title {
        font-size: 20px;
        font-weight: 500;
        line-height: 28px;
        margin-top: 30px;
        margin-bottom: 15px;
      }
      .author-info-des {
        font-size: 14px;
        font-weight: 400;
        line-height: 22px;
        color: #444d54;
      }
      .createdBy-updatedBy {
        color: #444d54;
        .createdBy {
          display: inline-block;
          margin: 10px 50px 20px 0px;
          .createdBy-label {
            font-size: 20px;
            font-weight: 400;
            line-height: 28px;
            margin-bottom: 5px;
          }
          .createdBy-des {
            font-size: 14px;
            font-weight: 400;
            line-height: 22px;
          }
        }
        .updatedBy {
          display: inline-block;
          margin: 10px 50px 20px 0px;
          .updatedBy-label {
            font-size: 20px;
            font-weight: 400;
            line-height: 28px;
            margin-bottom: 5px;
          }
          .updatedBy-des {
            font-size: 14px;
            font-weight: 400;
            line-height: 22px;
          }
        }
      }
    }

    .text-input.question-description {
      padding-top: 15px;
      width: 945px;
      font-size: 16px;
      .el-textarea__inner {
        min-height: 100px !important;
        font-size: 16px;
        border-width: 1px !important;
        border-radius: 5px;
        padding-top: 8px;
        padding-left: 10px;
        border-color: #d2d8dc;
        color: #606266;
        font-family: "Helvetica Neue", Helvetica, Arial, sans-serif;
        pointer-events: all !important;
        &::-webkit-scrollbar {
          width: 20px;
          cursor: default !important;
        }
        &::-webkit-scrollbar-thumb {
          border-radius: 160px;
          border: 2px solid transparent;
          border-top: 4px solid transparent;
          background-clip: content-box;
          background-color: #CAD2DA;
          cursor: default !important;
        }
        &::-webkit-scrollbar-track {
          background:#F3F6F8;
          border-top: 1px solid #CAD2DA;
          border-radius: 5px;
          cursor: default !important;
        }
        &::-webkit-scrollbar:vertical {
          width: 14px;
        }
        &::-webkit-scrollbar-thumb:vertical {
          border-radius: 160px;
          border: 2px solid transparent;
          border-left: 4px solid transparent;
          border-right: 4px solid transparent;
          background-clip: content-box;
          background-color: #CAD2DA;
        }
        &::-webkit-scrollbar-track:vertical {
          // border-left: 1px solid #CAD2DA;
          border-top: none !important;
        }
      }
      .el-textarea__inner::placeholder {
        font-size: 12px !important;
        font-family: "Helvetica Neue", Helvetica, Arial, sans-serif;
      }
      .el-textarea__inner:focus {
        // border-color: #D2D8DC !important;
        border-color: #1a1c1e !important;
      } 
    }
    .question-description-label {
      font-size: 11px;
      color: #444d54;
      position: absolute;
      top: 57px;
      left: 60px;
      background-color: #fff;
      padding: 0px 5px;
    }
    .option-title {
      display: block;
      padding: 10px 0px;
      min-height: 300px;

      .option-body {
        position: relative;
        display: flex;
        .option-body-content {
          position: relative;
          display: flex;
        }
        .btn-move-section,
        .btn-move-question {
          height: 36px;
          padding: 4px 8px;
          position: relative;
          top: 3px;
          cursor: pointer;
          border-radius: 5px;
          background-color: #fff;
          border: 1px solid #d9d9d9;
          margin-right: 12px;
          box-shadow: 0px 2px 0px 0px rgba(0, 0, 0, 0.02);
          .el-icon-arrow-up,
          .el-icon-arrow-down {
            font-size: 20px;
          }
          .el-icon-arrow-up:before {
            color: #8c8c8c;
          }
          .el-icon-arrow-down:before {
            color: #8c8c8c;
          }
        }
        .btn-move-section:disabled,
        .btn-move-question:disabled {
          opacity: 0.5;
          cursor: not-allowed !important;
        }
        .option-label-content {
          .option-label.el-input {
            position: relative;
            top: 20px;
            input.el-input__inner:focus {
              border-color: #1a1c1e !important;
            } 
          }
        }
        
        .option-label-title {
          font-size: 11px;
          position: absolute;
          top: 3px;
          left: 101px;
        }
        .option-value-title{
          font-size: 11px;
          position: absolute;
          top: 0px;
        }
        .option-auto-fail-title{
          font-size: 11px;
          position: absolute;
          top: -15px;
          left: 614px;
        }
        .div-phrase-content {
          position: relative;
          top: -7px;
          .option-description {
            margin: 27px 5px 5px 0px;
            width: 520px;

            .el-input__count {
              position: absolute;
              right: 0px;
              top: 78px;
            }
            .el-textarea .el-input__count {
              right: 0px !important;
            }
            .el-textarea__inner {
              min-height: 77px !important;
              font-family: "Helvetica Neue", Helvetica, Arial, sans-serif;
              pointer-events: all !important;
              &::-webkit-scrollbar {
                width: 20px;
                cursor: default !important;
              }
              &::-webkit-scrollbar-thumb {
                border-radius: 160px;
                border: 2px solid transparent;
                border-top: 4px solid transparent;
                background-clip: content-box;
                background-color: #CAD2DA;
                cursor: default !important;
              }
              &::-webkit-scrollbar-track {
                background:#F3F6F8;
                border-top: 1px solid #CAD2DA;
                border-radius: 5px;
                cursor: default !important;
              }
              &::-webkit-scrollbar:vertical {
                width: 14px;
              }
              &::-webkit-scrollbar-thumb:vertical {
                border-radius: 160px;
                border: 2px solid transparent;
                border-left: 4px solid transparent;
                border-right: 4px solid transparent;
                background-clip: content-box;
                background-color: #CAD2DA;
              }
              &::-webkit-scrollbar-track:vertical {
                // border-left: 1px solid #CAD2DA;
                border-top: none !important;
              }
            }
            .el-textarea__inner::placeholder {
              font-size: 12px !important;
              font-family: "Helvetica Neue", Helvetica, Arial, sans-serif;
            }
            .el-textarea__inner:focus {
              border-color: #1a1c1e !important;
            } 
            .el-tag {
              margin: 5px;
              background-color: #FAFAFA;
              border-color: #D9D9D9;
              color: var(--site-font-primary-color);
              padding: 0 8px;
              height: 25px;
              line-height: 25px;
              cursor: pointer;
              .el-tag__close {
                color: var(--site-font-primary-color);
              }
              .el-tag__close:hover {
                color: #FFF !important;
                // color: var(--site-font-primary-color);
                background-color: #989c9f;
              }
            }
            .el-tag.duplicate-phrase {
              color: #FA8C16;
              background-color: #FFF7E6;
              border-color: #FA8C16;
              .el-tag__close:hover {
                color: #FFF7E6 !important;
                background-color: #FA8C16;
              }
            }
            .el-tag.phrase-match-tag-update {
              color: #409EFF;
              border-color: #c6e2ff;
              background-color: #ecf5ff;
              .el-tag__close:hover {
                background-color: #409EFF;
              }
            }
            .button-new-tag {
              height: 25px;
              padding: 0 10px;
              line-height: 23px;
              font-size: 12px;
              border-width: 1px;
              border-style: dashed;
              border-radius: 4px;
            }
            .input-new-tag {
              width: fit-content;
              .el-input__inner {
                height: 25px;
                line-height: 25px;
                font-size: 12px;
                border-radius: 4px;
                padding: 0 10px;
              }
            }
          }
          .phrase-match-title {
            font-size: 11px;
            position: absolute;
            top: 12px;
          }
        }
        .el-dropdown {
          position: relative;
          font-size: 24px;
          top: 6px;
          height: 20px;

          .el-dropdown-link {
            .el-icon-three-dots {
              background-image: var(--header-icon-menu-three-dots);
              background-repeat: no-repeat;
              background-size: 26px;
              width: 20px;
              height: 24px;
              background-position-y: 0px;
              margin-left: 5px;
              cursor: pointer;
            }
            .el-icon-edit:before {
              content: "";
            }
            .el-icon-edit {
              background-image: var(--header-icon-edit-pencil);
              background-repeat: no-repeat;
              background-size: 20px;
              width: 20px;
              height: 20px;
              background-position-y: 2px;
              margin-left: 20px;
              cursor: pointer;
            }
            .el-icon-edit:before {
              content: "";
            }
          }
        }
      }
      .option-body.fade-out-border-color {
        background-color: transparent !important;
        transition: background-color 2.5s !important;
        text-decoration: none;
      }
      .option-label {
        width: fit-content;
        margin-right: 15px;
        input.el-input__inner {
          border-radius: 5px;
          width: 250px;
          border: 1px solid #D2D8DC !important;
          padding-left: 10px;
        }
        .el-input__count {
          position: absolute;
          top: 28px;
          left: -25px;
        }
      }
      .option-label-content .option-label.el-input.hasError {
        input.el-input__inner {
          border: 1px solid #ebafba !important;
          background-color: #fff4f4;
        }
        input.el-input__inner:focus {
          border-color: #ebafba !important;
        } 
      }
      .option-value {
        width: fit-content;
        input.el-input__inner {
          border-radius: 5px;
          width: 162px;
          border: 1px solid #D2D8DC !important;
          padding-left: 10px;
        }
      }
      .el-checkbox__input {
        margin-left: 15px;
      }
      span.el-checkbox__label {
        position: relative;
      }
      .el-checkbox:last-of-type {
        width: 105px;
        margin-right: 20px;
      }
      .option-kpi-section {
        display: inline-flex;
        align-items: center;
        span.label {
          position: relative;
          width: 90px;
          font-size: 14px;
        }
        .el-input__inner {
          font-size: 14px;
          border-width: 1px !important;
          border-radius: 5px;
          padding-left: 10px;
          height: 38px;
          border-color: #D2D8DC !important;
          width: 120px;
        }
      }
    }
    .option-body-bottom {
      margin-top: 20px;
      .div-na-content {
        display: flex;
        .na-option-description-content {
          position: relative;
          display: flex;
          .option-description {
            width: 520px;

            .el-input__count {
              position: absolute;
              right: 0px;
              top: 78px;
            }
            .el-textarea .el-input__count {
              right: 0px !important;
            }
            .el-textarea__inner {
              min-height: 77px !important;
              font-family: "Helvetica Neue", Helvetica, Arial, sans-serif;
              pointer-events: all !important;
              &::-webkit-scrollbar {
                width: 20px;
                cursor: default !important;
              }
              &::-webkit-scrollbar-thumb {
                border-radius: 160px;
                border: 2px solid transparent;
                border-top: 4px solid transparent;
                background-clip: content-box;
                background-color: #CAD2DA;
                cursor: default !important;
              }
              &::-webkit-scrollbar-track {
                background:#F3F6F8;
                border-top: 1px solid #CAD2DA;
                border-radius: 5px;
                cursor: default !important;
              }
              &::-webkit-scrollbar:vertical {
                width: 14px;
              }
              &::-webkit-scrollbar-thumb:vertical {
                border-radius: 160px;
                border: 2px solid transparent;
                border-left: 4px solid transparent;
                border-right: 4px solid transparent;
                background-clip: content-box;
                background-color: #CAD2DA;
              }
              &::-webkit-scrollbar-track:vertical {
                // border-left: 1px solid #CAD2DA;
                border-top: none !important;
              }
            }
            .el-textarea__inner::placeholder {
              font-size: 12px !important;
              font-family: "Helvetica Neue", Helvetica, Arial, sans-serif;
            }
            .el-textarea__inner:focus {
              border-color: #1a1c1e !important;
            } 
          }
          .phrase-match-title {
            font-size: 11px;
            position: absolute;
            top: -15px;
          }
        }
        .na-option-content {
          width: 365px;
        }
      }
      .btn-add-option {
        margin-top: 50px;
      }
    }
    .btn-add-option {
      display: block;
      cursor: pointer;
      margin-right: 10px;
      border: 0px;
      color: #fff;
      -webkit-box-shadow: none;
      box-shadow: none;
      background-color: #1890ff;
      border-radius: 5px;
      font-weight: 600;
      width: 120px;
      height: 32px;
    }
    .div-question-target.disabled {
      opacity: 0.5;
      pointer-events: none;
      background-color: #fafaff;
    }
    .title {
      font-weight: 500;
      font-size: 20px;
      line-height: 28px;
      padding-top: 35px;
      padding-bottom: 15px;
    }
    .description {
      font-weight: 400;
      font-size: 14px;
      line-height: 22px;
    }
    .div-option-title {
      padding-top: 30px;
      font-size: 20px;
      font-weight: 500;
      line-height: 28px;
    }
    .sub_description {
      max-width: 920px;
      font-weight: 400;
      font-size: 14px;
      line-height: 22px;
      padding-bottom: 15px;
      padding-top: 15px;
    }
    .el-checkbox__input.is-checked .el-checkbox__inner,
    .el-checkbox__input.is-indeterminate .el-checkbox__inner {
      border-radius: 2px;
      background-color: #1890ff;
    }
    .el-checkbox__input.is-checked + .el-checkbox__label {
      font-weight: 400;
      color: #1b1f22;
    }
    
    
      // background-color: #fbfbfb;
      .section-header {
        display: inline-flex;
        position: relative;
        // background-color: #fbfbfb;
        .section-name {
          // margin-left: 5px;
          .span-input-label {
            width: fit-content;
            font-size: 20px;
            position: relative;
            top: 8px;
            padding: 8px 10px;
          }
          .span-input-label.duplicate-section-name {
            background-color: #fff4f4;
          }
          .text-input.duplicate-section-name {
            border: 1px solid #ebafba !important;
            .el-input__inner {
              background-color: #fff4f4;
              // border: 1px solid #ebafba;
            }
          }
          .text-input {
            height: 34px !important;
            top: 3px;
            border: 1px solid #000 !important;
            border-radius: 5px;
            input.el-input__inner {
              // border: 1px solid transparent;
              height: 34px;
              border: 0px solid #000 !important;
              border-width: 0px 0px 0px 0px !important;
              border-radius: 5px;
            }
          }
          .el-icon-edit {
            background-image: var(--header-icon-edit-pencil);
            background-repeat: no-repeat;
            background-size: 20px;
            width: 20px;
            height: 20px;
            background-position-y: 2px;
            margin-left: 20px;
            cursor: pointer;
          }
          .el-icon-edit:before {
            content: "";
          }
        }
        .warning-msg {
          font-size: 11px;
          color: #ebafba;
          position: absolute;
          top: 40px;
          left: 100px;
        }
        .reminding-msg {
          font-size: 11px;
          color: #444d54;
          position: absolute;
          top: 40px;
          left: 100px;
        }
        .section-weight-div, .section-target-div {
          position: relative;
          .input-section-weight, .input-section-target {
            height: 34px;
            line-height: 40px;
            outline: 0;
            padding: 0 15px;
            width: 145px;
            font-size: 16px;
            line-height: normal;
            transition: border-color 0.2s
              cubic-bezier(0.645, 0.045, 0.355, 1);
            border: 1px solid #dcdfe6;
            border-width: 1px !important;
            border-radius: 5px;
            padding-left: 10px;
            margin-left: 50px;
            border-color: #d2d8dc;
            position: relative;
            top: 2px;
          }
          .input-section-target {
            margin-left: 10px;
          }
          .section-weight-label, .section-target-label {
            font-size: 11px;
            color: #444d54;
            position: absolute;
            top: -5px;
            left: 61px;
            background-color: transparent;
            padding: 0px 5px;
            z-index: 99;
          }
          .section-target-label {
            left: 18px;
          }
          .section-weight-label-background, .section-target-label-background {
            width: 105px;
            height: 10px;
            position: absolute;
            top: 2px;
            left: 61px;
            background-color: #fff;
          }
          .section-target-label-background {
            left: 18px;
          }
          .section-weight-label-background.disabled, .section-target-label-background.disabled {
            background-color: #fafaff;
          }
          .section-weight-label-background.no-value, .section-target-label-background.no-value {
            background-color: #fff4f4 !important;
          }
        }
      }
      .section-content {
        padding-left: 50px;
        padding-bottom: 20px;
        // background-color: #fbfbfb;
        .add-question-title {
          padding: 30px 0px 0px 0px;
        }
        .div-add-question-btn {
          padding: 20px 0px 10px 0px;
          .btn-add-question {
            cursor: pointer;
            margin-right: 10px;
            border: 0px;
            color: #fff;
            box-shadow: none;
            background-color: #1890ff;
            border-radius: 5px;
            font-weight: 600;
            width: 150px;
            height: 32px;
          }
        }
        .div-balance-question-weight-btn {
          .btn-balance-question-weight {
            cursor: pointer;
            margin-right: 10px;
            border: 0px;
            color: #1b1f22;
            -webkit-box-shadow: none;
            box-shadow: none;
            background-color: #bfbfbf;
            border-radius: 5px;
            font-weight: 400;
            width: 215px;
            height: 32px;
            margin-right: 300px;
          }

          .question-weight-percentage {
            font-size: 24px;
            font-weight: 700;
            position: relative;
            top: 5px;

            .el-icon-weight-info {
              position: relative;
              background-image: var(--header-icon-elv-form-weight-pass);
              background-repeat: no-repeat;
              background-size: 24px;
              width: 24px;
              height: 24px;
              top: 3px;
              left: 10px;
            }
          }
          .question-weight-percentage.red {
            color: #a6182d;
            .el-icon-weight-info {
              background-image: var(--header-icon-elv-form-weight-warning);
            }
          }
        }

        .questions {
          .question-body.fade-out-border-color {
            //border: 2px solid transparent !important; /* <- here */
            //transition: border 2.5s !important;
            background-color: transparent !important;
            transition: background-color 2.5s !important;
            text-decoration: none;
          }
          .question-body {
            padding-top: 30px;
            margin-top: 10px;
            .question-header {
              display: inline-flex;
              width: 100%;
              position: relative;
              .category-question-section {
                width: calc(100% - 185px);
                min-width: 637px;
                position: relative;
              }
              .phrase-match-question-text {
                display: inline-block;
                margin-left: auto;
                position: relative;
                .phrase-input {
                  font-size: 16px;
                  width: 428px;
                  input.el-input__inner {
                    font-size: 16px;
                    border-width: 1px !important;
                    border-radius: 5px;
                    padding-left: 10px;
                    border-color: #dcd4d2;
                  }
                  .el-input__inner:focus {
                    border-color: #1a1c1e !important;
                  }
                }
                .phrase-input.no-value {
                  .el-input__inner {
                    background-color: #fff4f4;
                    border: 1px solid #ebafba;
                  }
                  .el-input__inner:focus {
                    border-color: #f03106 !important;
                  }
                }
                .warning-msg {
                  font-size: 11px;
                  color: #ebafba;
                  position: absolute;
                  top: 41px;
                  left: 2px;
                }
                .reminding-msg {
                  font-size: 11px;
                  color: #444d54;
                  position: absolute;
                  top: 41px;
                  left: 2px;
                }
              }
              .category-dropdown,
              .question-dropdown {
                margin-right: 10px;
                .el-input--mini .el-input__inner {
                  font-size: 14px;
                  border-width: 1px !important;
                  border-radius: 5px;
                  padding-left: 10px;
                  height: 40px;
                  border-color: #d2d8dc !important;
                }
                .el-select__caret {
                  color: #1b1f22;
                }
                .el-input.el-input--mini.is-disabled {
                  input.el-input__inner {
                    background-color: #f5f7fa;
                    border-color: #d2d8dc !important;
                  }
                }
              }

              .category-dropdown.no-value,
              .question-dropdown.no-value {
                .el-input__inner {
                  background-color: #fff4f4;
                  border: 1px solid #ebafba;
                }
              }

              .category-dropdown {
                width: 150px;
              }

              .question-dropdown {
                margin-right: 0px !important;
                width: calc(100% - 370px);
                min-width: 115px;
              }

              .input-question-weight {
                height: 37px;
                outline: 0;
                padding: 0 15px;
                width: 145px;
                font-size: 16px;
                line-height: normal;
                transition: border-color 0.2s
                  cubic-bezier(0.645, 0.045, 0.355, 1);
                border: 1px solid #dcdfe6;
                border-width: 1px !important;
                border-radius: 5px;
                padding-left: 10px;
                border-color: #d2d8dc;
                margin-left: 10px;
              }
              .question-weight-label {
                font-size: 11px;
                color: #444d54;
                position: absolute;
                top: -6px;
                left: 730px;
                background-color: transparent;
                padding: 0px 5px;
                z-index: 99;
              }
              .question-weight-label-background {
                width: 112px;
                height: 10px;
                position: absolute;
                top: 0px;
                left: 730px;
                background-color: #fff;
              }
              .question-weight-label-background.disabled {
                background-color: #fafaff;
              }
              .question-weight-label-background.no-value {
                background-color: #fff4f4 !important;
              }
              .el-dropdown {
                position: relative;
                font-size: 24px;
                top: 5px;
                .el-dropdown-link {
                  .el-icon-three-dots {
                    background-image: var(--header-icon-menu-three-dots);
                    background-repeat: no-repeat;
                    background-size: 26px;
                    width: 20px;
                    height: 24px;
                    background-position-y: 0px;
                    margin-left: 5px;
                    cursor: pointer;
                  }
                  .el-icon-edit:before {
                    content: "";
                  }
                  .el-icon-edit {
                    background-image: var(--header-icon-edit-pencil);
                    background-repeat: no-repeat;
                    background-size: 20px;
                    width: 20px;
                    height: 20px;
                    background-position-y: 2px;
                    margin-left: 20px;
                    cursor: pointer;
                  }
                  .el-icon-edit:before {
                    content: "";
                  }
                }
              }

              .btn-question-menu {
                .el-icon-three-dots {
                  background-image: var(--header-icon-menu-three-dots);
                  background-repeat: no-repeat;
                  background-size: 20px;
                  width: 20px;
                  height: 20px;
                  background-position-y: 2px;
                  margin-left: 20px;
                  cursor: pointer;
                }
                .el-icon-edit:before {
                  content: "";
                }
                .el-icon-edit {
                  background-image: var(--header-icon-edit-pencil);
                  background-repeat: no-repeat;
                  background-size: 20px;
                  width: 20px;
                  height: 20px;
                  background-position-y: 2px;
                  margin-left: 20px;
                  cursor: pointer;
                }
                .el-icon-edit:before {
                  content: "";
                }
              }
            }
            .category-title {
              font-size: 11px;
              position: absolute;
              left: 102px;
              top: -15px;
            }
            .question-title {
              font-size: 11px;
              position: absolute;
              left: 262px;
              top: -15px;
            }
            .question-content.read-only {
              padding-left: 0px;
              pointer-events: none;
              opacity: 0.7;
            }
            .question-content {
              padding: 5px;
              padding-left: 100px;
              position: relative;

              .text-input.question-description {
                padding-top: 15px;
                width: calc(100% - 10px);
                font-size: 16px;
                max-width: 690px;
                input.el-input__inner {
                  font-size: 16px;
                  border-width: 1px !important;
                  border-radius: 5px;
                  padding-left: 10px;
                  border-color: #d2d8dc;
                  font-family: "Helvetica Neue", Helvetica, Arial, sans-serif;
                }
                .el-input__inner:focus {
                  // border-color: #D2D8DC !important;
                  border-color: #1a1c1e !important;
                }
              }
              .question-description-label {
                font-size: 11px;
                color: #444d54;
                position: absolute;
                top: 13px;
                left: 122px;
                background-color: #fff;
                padding: 0px 5px;
              }
              .warning-msg {
                font-size: 11px;
                color: #ebafba;
                position: absolute;
                top: 61px;
                left: 100px;
              }
              .reminding-msg {
                font-size: 11px;
                color: #444d54;
                position: absolute;
                top: 61px;
                left: 100px;
              }
              .add-question-title {
                padding: 30px 0px 0px 0px;
              }
              .customer-effort-question {
                .customer-effort-operator-dropdown.no-value {
                  .el-input__inner {
                    background-color: #fff4f4;
                    border: 1px solid #ebafba;
                  }
                }
                .input-customer-effort-value.no-value {
                  background-color: #fff4f4;
                  border: 1px solid #ebafba;
                }
              }
              
              .phrase-match {
                span.label {
                  position: relative;
                  width: 90px;
                  font-size: 14px;
                }
                .el-input__inner {
                  font-size: 14px;
                  border-width: 1px !important;
                  border-radius: 5px;
                  padding-left: 10px;
                  height: 38px;
                  border-color: #D2D8DC !important;
                  width: 120px;
                }
                .div-phrase-match {
                  margin-top: 10px;
                  display: inline-flex;
                  position: relative;

                  .match-type {
                    margin-right: 20px;
                  }
                  .phrase-match-body {
                    .el-input__inner {
                      width: 300px;
                    }
                  }
                  .phrase-match-type-title {
                    font-size: 11px;
                    position: absolute;
                    top: -15px;
                  }
                  .phrase-match-sent-by-title {
                    font-size: 11px;
                    position: absolute;
                    left: 142px;
                    top: -15px;
                  }
                }
                .question-dropdown.no-value {
                  .el-input__inner {
                    background-color: #fff4f4;
                    border: 1px solid #ebafba;
                  }
                }
              }
              .answer-content {
                display: none;
                margin-top: 30px;
                .btn-add-answer {
                  cursor: pointer;
                  margin-right: 10px;
                  border: 0px;
                  color: #fff;
                  box-shadow: none;
                  background-color: #1890ff;
                  border-radius: 5px;
                  font-weight: 600;
                  width: 150px;
                  height: 32px;
                }
                .div-add-answer-btn {
                  margin-top: 15px;
                }
              }

              .customer-effort-question {
                padding-top: 30px;
                position: relative;
                display: flex;
                align-items: center;
                .customer-effort-option-label {
                  font-weight: 700;
                }
                .customer-effort-operator-title {
                  font-size: 11px;
                  position: relative;
                  right: 263px;
                  top: -30px;
                }
                .threshold-section {
                  position: relative;
                  width: 400px;
                  .customer-effort-threshold-des {
                    font-size: 11px;
                    position: absolute;
                    left: 10px;
                    top: 41px;
                  }
                  .customer-effort-threshold-title {
                    font-size: 11px;
                    position: absolute;
                    left: 11px;
                    top: -15px;
                  }
                }
                .customer-effort-operator-dropdown {
                  margin-left: 20px;
                  // margin-right: 10px;
                  width: 65px;
                  .el-input--mini .el-input__inner {
                    font-size: 14px;
                    border-width: 1px !important;
                    border-radius: 5px;
                    padding-left: 10px;
                    height: 40px;
                    border-color: #d2d8dc !important;
                  }
                  .el-select__caret {
                    color: #1b1f22;
                  }
                }
                .input-customer-effort-value {
                  height: 37px;
                  outline: 0;
                  padding: 0 15px;
                  width: 238px;
                  font-size: 16px;
                  line-height: normal;
                  transition: border-color 0.2s
                    cubic-bezier(0.645, 0.045, 0.355, 1);
                  border: 1px solid #dcdfe6;
                  border-width: 1px !important;
                  border-radius: 5px;
                  padding-left: 10px;
                  border-color: #d2d8dc;
                  margin-left: 10px;
                }
              }
            }
          }
        }
      }
    
    .section-blank-space {
      width: 15px;
    }
    .question-blank-space {
      width: 0px;
    }

    .vue-numeric.no-value {
      background-color: #fff4f4;
      border: 1px solid #ebafba !important;
    }
    .section-question {
      .slider {
        width: 350px;
        .slider-block {
          width: 350px;
          position: relative;
          .el-slider {
            width: 350px;
            padding: 30px 0px;
            .el-slider__runway {
              height: 2px;
              background-color: #dcdfe6;
              .el-slider__bar {
                height: 2px;
                background-color: #91D5FF;
              }
              .el-slider__button-wrapper {
                height: 6px;
                width: 6px;
                top: -10px;
                z-index: 100;
                .el-slider__button {
                  width: 10px;
                  height:10px;
                  border: 2px solid #91D5FF;
                  // border: 1px solid #0b2b3b;
                  // background-color: #0b2b3b;
                }
              }
            }
          }
          .sphere {
            height: 25px;
            width: 25px;
            border-radius: 0 50% 50% 50%;
            text-align: center;
            vertical-align: middle;
            position: absolute;
            display: inline-block;
            transform:rotate(225deg);
            background:transparent;
            div.text-value {
              position: relative;
              transform: rotate(135deg);
              color: #000;
              left: 0px;
              top: 6px;
              font-weight: 500;
              font-size: 11px;
            }
          }
          .target-start {
            color: #919191;
            left: 0px;
            top: 20px;
            font-weight: 500;
            font-size: 11px;
            position: absolute;
          }
          .target-end {
            color: #919191;
            left: 344px;
            top: 20px;
            font-weight: 500;
            font-size: 11px;
            position: absolute;
          }
          // .sphere.button1 {
          //   background-image: var(--form-editor-target-icon);
          //   background-repeat: no-repeat;
            
          // }
          .target_2 {
            height: 25px;
            width: 25px;
            position: absolute;
            background-image: var(--form-editor-target-icon);
            background-repeat: no-repeat;
          }
        }
      }
      .target-title {
        padding-top: 10px;
        padding-bottom: 20px;
        font-size: 14px;
      }
      .target-des {
        font-size: 14px;
        margin-bottom: 25px;
       
        .target-item {
          width: 530px;
          display: inline-flex;
        }
        span.value {
          font-weight: 700;
          width: 80px;
          display: flex;
          align-items: center;
        }
        .text-input.question-target.empty {
          input.el-input__inner {
            border: 1px solid #ebafba !important;
            background-color: #fff4f4;
          }
        }
        input.el-input__inner {
          font-size: 16px;
          border-width: 1px !important;
          border-radius: 5px;
          padding-left: 10px;
          border-color: #d2d8dc;
          width: 450px;
        }
        .el-input__count .el-input__count-inner {
          position: relative;
          top: 30px;
        }
      }
    }
   
    .add-section-title {
      padding: 30px 0px 0px 0px;
    }
    .div-add-section-btn {
      padding: 20px 0px 40px 0px;
      .btn-add-section {
        cursor: pointer;
        margin-right: 10px;
        border: 0px;
        color: #fff;
        box-shadow: none;
        background-color: #1890ff;
        border-radius: 5px;
        font-weight: 600;
        width: 150px;
        height: 32px;
      }
      .btn-balance-section-weight {
        cursor: pointer;
        margin-right: 10px;
        border: 0px;
        color: #1b1f22;
        -webkit-box-shadow: none;
        box-shadow: none;
        background-color: #bfbfbf;
        border-radius: 5px;
        font-weight: 400;
        width: 215px;
        height: 32px;
        margin-right: 50px;
      }
      .section-weight-percentage {
        font-size: 24px;
        font-weight: 700;
        position: relative;
        top: 5px;
        .el-icon-weight-info {
          position: relative;
          background-image: var(--header-icon-elv-form-weight-pass);
          background-repeat: no-repeat;
          background-size: 24px;
          width: 24px;
          height: 24px;
          top: 3px;
          left: 10px;
        }
      }
      .section-weight-percentage.red {
        color: #a6182d;
        .el-icon-weight-info {
          background-image: var(--header-icon-elv-form-weight-warning);
        }
      }
    }
  }
  .new-question-body {
    .question-des-warning-msg {
      font-size: 11px;
      color: #ebafba;
      position: absolute;
      top: 166px;
      left: 40px;
    }
    .question-des-reminding-msg {
      font-size: 11px;
      color: #444d54;
      position: absolute;
      top: 166px;
      left: 40px;
    }
  }
  .el-loading-mask {
    background-color: #fff !important;
    .el-loading-spinner {
      top: 45% !important;
      margin-top: 0px;
      background: var(--site-loading-icon);
      background-repeat: no-repeat;
      background-size: 25px;
      background-position: center;
      .circular {
        display: inline-block;
        width: 90px;
        height: 90px;
      }
    }
  }

  .popup-wrapper {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 9999;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .popup-body {
    margin-top: -130px;
    background: white;
    display: flex;
    flex-direction: column;
    max-height: calc(100% - 64px);
    max-width: 600px;
    border-radius: 4px;
    font-family: "Roboto", "Helvetica", "Arial", sans-serif;
    box-shadow: 0px 11px 15px -7px rgba(0, 0, 0, 0.2),
      0px 24px 38px 3px rgba(0, 0, 0, 0.14),
      0px 9px 46px 8px rgba(0, 0, 0, 0.12);

    .title {
      font-size: 1.25rem;
      font-weight: 600;
      line-height: 1.6;
      letter-spacing: 0.0075em;
      padding: 16px 24px;
      text-align: left;
    }
    .desc {
      color: rgba(0, 0, 0, 0.54);
      padding: 8px 24px;
      font-size: 1rem;
      font-weight: 400;
      line-height: 1.5;
      letter-spacing: 0.00938em;
      margin-bottom: 12px;
      text-align: left;
    }
    .footer {
      padding: 8px;
      display: flex;
      justify-content: flex-end;
      .el-button {
        border-radius: 4px !important;
        padding: 12px 16px !important;
        background-color: #193b5c;
        border: none;
        box-shadow: 0px 3px 1px -2px rgba(0, 0, 0, 0.2),
          0px 2px 2px 0px rgba(0, 0, 0, 0.14),
          0px 1px 5px 0px rgba(0, 0, 0, 0.12);
      }
    }
    .el-button {
      font-weight: 600 !important;
      width: 64px;
    }
    .no-action {
      background: #e0e0e0 !important;
      color: black !important;
    }
  }
}
// @media screen and (min-width: 1800px) {
//   .wy-evaluation-new-question {
//     margin: 0px 2%;
//   }
// }
.el-dropdown-menu.elv-question-question-menu {
  min-width: 190px;
  .el-dropdown-menu__item.is-disabled {
    opacity: 0.5;
  }
}
.elv-question-question-menu,
.wy-auto-qa-elv-question-category-questions {
  .el-dropdown-menu__item {
    color: #003b5c;
  }

  .el-select-dropdown__item.selected {
    color: #006fe7;
    font-weight: 400;
  }
  .el-dropdown-menu__item:hover {
    background-color: rgba(0, 59, 92, 0.04);
    color: #003b5c;
  }
  .popper__arrow {
    display: none;
  }
}

.elv-question-question-menu,
.wy-auto-qa-elv-question-category-questions.questions {
  .el-select-dropdown__item.selected {
    display: block !important;
  }
  .el-select-dropdown__item.is-disabled {
    display: none;
  }
}

.el-message.evaluation-question-duplicate-name-warning-msg,
.el-message.evaluation-question-internal-error-warning-msg,
.evaluation-question-publish-warning-msg {
  background-color: #a6182d;
  padding: 15px 35px 15px 20px;
  .el-icon-info {
    display: none;
  }
  .el-message__content {
    padding-right: 50px;
    font-weight: 700;
    color: #fff;
  }
  .el-message__closeBtn {
    color: #fff;
    font-size: 20px;
    font-weight: 700;
  }
}

.el-message.evaluation-question-save-success-msg {
  background-color: #4caf50;
  .el-icon-info {
    display: none;
  }
  .el-message__content {
    padding-right: 50px;
    font-weight: 700;
    color: #fff;
  }
  .el-message__closeBtn {
    color: #fff;
    font-size: 20px;
    font-weight: 700;
  }
}

.elv-question-question-menu,
.wy-auto-qa-elv-question-category-questions.options-operator {
  .el-select-dropdown__item.selected {
    color: #006fe7;
    font-weight: 400;
  }
}

.wy-auto-qa-elv-question-questions-options-auto-fail-level {
  .el-select-dropdown__item.selected {
    color: #006FE7;
    font-weight: 400;
  }
}

.wy-auto-qa-elv-question-questions-phrase-match-sent-by,.wy-auto-qa-elv-question-questions-phrase-match-match-type {
  .el-select-dropdown__item.selected {
    color: #006FE7;
    font-weight: 400;
  }
}

.el-dialog__wrapper {
  .el-dialog.evl-question-dialog-modal {
    width: 600px !important;
    height: 192px;
    .el-dialog__title {
      font-size: 20x;
      font-family: "Roboto", "Helvetica", "Arial", sans-serif;
      font-weight: 500;
      line-height: 1.6;
    }
    .el-dialog__body {
      padding: 25px 20px;
    }
    .el-dialog__headerbtn {
      display: none;
    }
    .dialog-footer {
      button {
        margin-right: 10px;
        border: 0px;
        box-shadow: none;
        border-radius: 2px;
        font-weight: 400;
        color: #000;
        background-color: #fff;
        border: 1px solid #d9d9d9;
      }
      button:disabled {
        color: rgba(0, 0, 0, 0.26);
        background-color: rgba(0, 0, 0, 0.12);
        cursor: not-allowed;
      }
      .btn-dialog-cancel {
        cursor: pointer;
        width: 82px;
      }
      .btn-dialog-confirm {
        cursor: pointer;
        width: 82px;
        float: right;
      }
    }
  }
}
.el-container.read-only {
  .question-body .question-header {
    .question-weight-label,
    .question-weight-label-background {
      left: 725px !important;
    }
  }
  .na-option-content {
    width: 265px !important;
  }
  .question-description-label {
    left: 56px !important;
  }
  .category-title {
    left: 2px !important;
  }
  .question-title {
    left: 162px !important;
  }
  .option-label-title {
    left: 0px !important;
  }
}
.question-editor-target-range-tooltip.el-tooltip__popper.is-dark {
  background-color: #0b2b3b !important;
    display: flex !important;
    -webkit-box-pack: center;
    -ms-flex-pack: center;
    justify-content: center;
    height: 12px;
    width: 12px;
    border-radius: 0 50% 50% 50%;
    text-align: center;
    vertical-align: middle;
    display: inline-block;
    margin: 0% 0% 40px 23px;
    -webkit-transform: rotate(225deg);
    transform: rotate(225deg);
  span {
    position: relative;
    left: 0px;
    transform: rotate(135deg);
  }
  .popper__arrow {
    top: 2px !important;
    left: 10px !important;
  }
}

.wy-evaluation-question-editor-question-description-popper {
  max-width: 544px;
  font-size: 11.2px;
}

</style>
